import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type { IMatchScheduleItem, IScheduleConstraints, IScheduleDayConstraints } from '../../types/schedule';
import { isMatchScheduledOnGrid, getTimeDateFromFormikInput } from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import { getVenueLocalParts } from '../../utils/tournamentDateTime';
import {
  alignMatchesToTournamentDays,
  alignScheduleDaysToTournament,
  detectScheduleDayMismatch,
} from './scheduleDayAlign';

/** Re-encode an instant so local wall-clock in newTz matches what it was in oldTz. */
export function remapInstantAcrossTimeZone(
  iso: string | Date,
  oldTimeZone: string,
  newTimeZone: string
): Date {
  const date = new Date(iso);
  const local = formatInTimeZone(date, oldTimeZone, 'yyyy-MM-dd HH:mm:ss');
  return fromZonedTime(local.replace(' ', 'T'), newTimeZone);
}

export function remapOptionalInstantAcrossTimeZone(
  value: string | Date | null | undefined,
  oldTimeZone: string,
  newTimeZone: string
): Date | null | undefined {
  if (value == null) return value;
  return remapInstantAcrossTimeZone(value, oldTimeZone, newTimeZone);
}

export function remapConstraintsAcrossTimeZone(
  constraints: IScheduleConstraints,
  oldTimeZone: string,
  newTimeZone: string
): IScheduleConstraints {
  const remap = (value: string | Date) =>
    remapInstantAcrossTimeZone(value, oldTimeZone, newTimeZone).toISOString();

  return {
    ...constraints,
    scheduleTimezone: newTimeZone,
    tournamentStartTime: remap(constraints.tournamentStartTime),
    tournamentEndTime: remap(constraints.tournamentEndTime),
    days: constraints.days.map((day) => ({
      ...day,
      startTime: remap(day.startTime),
      endTime: remap(day.endTime),
    })),
  };
}

export function remapMatchAcrossTimeZone(
  match: IMatchScheduleItem,
  oldTimeZone: string,
  newTimeZone: string
): IMatchScheduleItem {
  const remapIso = (value: string | null | undefined): string | null => {
    if (!value) return null;
    return remapInstantAcrossTimeZone(value, oldTimeZone, newTimeZone).toISOString();
  };

  return {
    ...match,
    scheduleDate: remapIso(match.scheduleDate ?? match.startDate ?? null),
    startDate: remapIso(match.startDate ?? match.scheduleDate ?? null),
    scheduleEndDate: remapIso(match.scheduleEndDate ?? null),
    endDate: remapIso(match.endDate) ?? match.endDate,
  };
}

export type TimezoneMigrationPreviewLine = {
  label: string;
  before: string;
  after: string;
};

export type TournamentDateInput = {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  registrationOpen?: string | Date | null;
  registrationClose?: string | Date | null;
  lastCancelationDate?: string | Date | null;
};

export type TimezoneMigrationResult = {
  remappedTournamentDates: ReturnType<typeof remapTournamentDatesAcrossTimeZone>;
  remappedConstraints: IScheduleConstraints | null;
  alignedConstraints: IScheduleConstraints | null;
  alignedMatches: IMatchScheduleItem[];
  unscheduledCount: number;
};

export function countMatchesUnscheduledByAlign(
  beforeAlign: IMatchScheduleItem[],
  afterAlign: IMatchScheduleItem[]
): number {
  const afterScheduledIds = new Set(
    afterAlign.filter(isMatchScheduledOnGrid).map((match) => match.id)
  );
  return beforeAlign.filter(
    (match) => isMatchScheduledOnGrid(match) && !afterScheduledIds.has(match.id)
  ).length;
}

export function migrateScheduleForTimezoneChange(options: {
  tournamentDates: TournamentDateInput;
  constraints: IScheduleConstraints | null;
  scheduledMatches: IMatchScheduleItem[];
  oldTimeZone: string;
  newTimeZone: string;
}): TimezoneMigrationResult {
  const { tournamentDates, constraints, scheduledMatches, oldTimeZone, newTimeZone } = options;

  const remappedTournamentDates = remapTournamentDatesAcrossTimeZone(
    tournamentDates,
    oldTimeZone,
    newTimeZone
  );

  const remappedMatches = scheduledMatches.map((match) =>
    remapMatchAcrossTimeZone(match, oldTimeZone, newTimeZone)
  );

  if (!constraints || !remappedTournamentDates.startDate || !remappedTournamentDates.endDate) {
    return {
      remappedTournamentDates,
      remappedConstraints: null,
      alignedConstraints: null,
      alignedMatches: remappedMatches,
      unscheduledCount: 0,
    };
  }

  const remappedConstraints = remapConstraintsAcrossTimeZone(
    constraints,
    oldTimeZone,
    newTimeZone
  );

  const alignedConstraints = alignScheduleDaysToTournament(
    remappedConstraints,
    remappedTournamentDates.startDate!,
    remappedTournamentDates.endDate!,
    newTimeZone
  );

  const alignedMatches = alignMatchesToTournamentDays(
    remappedMatches,
    remappedConstraints,
    alignedConstraints,
    newTimeZone
  );

  return {
    remappedTournamentDates,
    remappedConstraints,
    alignedConstraints,
    alignedMatches,
    unscheduledCount: countMatchesUnscheduledByAlign(remappedMatches, alignedMatches),
  };
}

export type TimezoneMigrationPreview = {
  lines: TimezoneMigrationPreviewLine[];
  unscheduledCount: number;
};

export function buildTimezoneMigrationPreview(
  constraints: IScheduleConstraints | null,
  scheduledMatches: IMatchScheduleItem[],
  oldTimeZone: string,
  newTimeZone: string,
  tournamentDates?: TournamentDateInput
): TimezoneMigrationPreview {
  if (!tournamentDates?.startDate || !tournamentDates?.endDate) {
    return { lines: [], unscheduledCount: 0 };
  }

  const migration = migrateScheduleForTimezoneChange({
    tournamentDates,
    constraints,
    scheduledMatches,
    oldTimeZone,
    newTimeZone,
  });

  const lines: TimezoneMigrationPreviewLine[] = [];
  const toIso = (value: string | Date) =>
    typeof value === 'string' ? value : value.toISOString();
  const formatLocal = (iso: string, tz: string) =>
    formatInTimeZone(new Date(iso), tz, 'yyyy-MM-dd HH:mm');

  const addLine = (label: string, beforeIso: string | Date, afterIso: string | Date) => {
    lines.push({
      label,
      before: formatLocal(toIso(beforeIso), oldTimeZone),
      after: formatLocal(toIso(afterIso), newTimeZone),
    });
  };

  addLine('Tournament start', tournamentDates.startDate, migration.remappedTournamentDates.startDate!);
  addLine('Tournament end', tournamentDates.endDate, migration.remappedTournamentDates.endDate!);

  const day0Before = constraints?.days?.[0];
  const day0After = migration.alignedConstraints?.days?.[0];
  if (day0Before && day0After) {
    addLine('Day 1 start', day0Before.startTime, day0After.startTime);
    addLine('Day 1 end', day0Before.endTime, day0After.endTime);
  }

  const sortedBefore = [...scheduledMatches]
    .filter((m) => m.scheduleDate || m.startDate)
    .sort(
      (a, b) =>
        new Date(a.scheduleDate || a.startDate!).getTime() -
        new Date(b.scheduleDate || b.startDate!).getTime()
    );

  const sortedAfter = [...migration.alignedMatches]
    .filter((m) => m.scheduleDate || m.startDate)
    .sort(
      (a, b) =>
        new Date(a.scheduleDate || a.startDate!).getTime() -
        new Date(b.scheduleDate || b.startDate!).getTime()
    );

  const exampleBefore = sortedBefore[0];
  const exampleAfter = sortedAfter[0];
  if (exampleBefore && exampleAfter) {
    addLine(
      'Example match',
      exampleBefore.scheduleDate || exampleBefore.startDate!,
      exampleAfter.scheduleDate || exampleAfter.startDate!
    );
  }

  const earlyMorningBefore = sortedBefore.find((m) => {
    const iso = m.scheduleDate || m.startDate;
    if (!iso) return false;
    const hhmm = formatInTimeZone(new Date(iso), oldTimeZone, 'HH:mm');
    const [h] = hhmm.split(':').map(Number);
    return h < 6;
  });
  const earlyMorningAfter =
    earlyMorningBefore &&
    migration.alignedMatches.find((m) => m.id === earlyMorningBefore.id);

  if (
    earlyMorningBefore &&
    earlyMorningAfter &&
    earlyMorningBefore.id !== exampleBefore?.id
  ) {
    addLine(
      'Early morning match',
      earlyMorningBefore.scheduleDate || earlyMorningBefore.startDate!,
      earlyMorningAfter.scheduleDate || earlyMorningAfter.startDate!
    );
  }

  return {
    lines: lines.slice(0, 6),
    unscheduledCount: migration.unscheduledCount,
  };
}

export function remapTournamentDatesAcrossTimeZone(
  dates: {
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    registrationOpen?: string | Date | null;
    registrationClose?: string | Date | null;
    lastCancelationDate?: string | Date | null;
  },
  oldTimeZone: string,
  newTimeZone: string
) {
  const remapIso = (value: string | Date | null | undefined): string | null | undefined => {
    if (value == null) return value;
    return remapInstantAcrossTimeZone(value, oldTimeZone, newTimeZone).toISOString();
  };

  return {
    startDate: remapIso(dates.startDate),
    endDate: remapIso(dates.endDate),
    registrationOpen: remapIso(dates.registrationOpen),
    registrationClose: remapIso(dates.registrationClose),
    lastCancelationDate: remapIso(dates.lastCancelationDate),
  };
}

export function countScheduledMatches(matches: IMatchScheduleItem[]): number {
  return matches.filter((m) => (m.scheduleDate || m.startDate) && m.court).length;
}

export function requiresTimezoneMigrationPreview(input: {
  hasTournamentDates: boolean;
  hasConstraints: boolean;
  isScheduled: boolean;
  matchCount: number;
}): boolean {
  return (
    input.hasConstraints ||
    input.isScheduled ||
    input.matchCount > 0 ||
    input.hasTournamentDates
  );
}

export function shouldBlockTimezoneChangeWhenPreviewUnavailable(
  requiresPreview: boolean,
  constraintsFetchFailed: boolean,
  scheduleFetchFailed: boolean
): boolean {
  return requiresPreview && (constraintsFetchFailed || scheduleFetchFailed);
}

function formatHhmmInTimeZone(iso: string | Date, timeZone: string): string {
  return formatInTimeZone(new Date(iso), timeZone, 'HH:mm');
}

function parseHhmmToMinutes(hhmm: string): number {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return hours * 60 + minutes;
}

function sessionSpanMinutes(start: string, end: string): number {
  const startM = parseHhmmToMinutes(start);
  const endM = parseHhmmToMinutes(end);
  if (endM > startM) return endM - startM;
  return 24 * 60 - startM + endM;
}

/** Typical venue session window (rejects midnight→noon TZ-mismatch artifact). */
export function looksLikeVenueSession(start: string, end: string): boolean {
  if (looksLikeCorruptTzArtifact(start, end)) return false;
  const startM = parseHhmmToMinutes(start);
  const span = sessionSpanMinutes(start, end);
  return startM >= 5 * 60 && span >= 6 * 60;
}

/** Long intentional day window, including early-morning starts (e.g. 01:00–23:45). */
function looksLikeIntentionalDaySession(start: string, end: string): boolean {
  if (looksLikeCorruptTzArtifact(start, end)) return false;
  return sessionSpanMinutes(start, end) >= 6 * 60;
}

function boundsAuthoredInStaleTimezone(
  inStale: { start: string; end: string },
  inTournament: { start: string; end: string }
): boolean {
  const staleStartM = parseHhmmToMinutes(inStale.start);
  const tournamentStartM = parseHhmmToMinutes(inTournament.start);
  // Early-morning stale session displayed as afternoon in tournament TZ (e.g. 01:00 JHB → 16:00 LA).
  return (
    staleStartM < 12 * 60 &&
    tournamentStartM >= 12 * 60 &&
    tournamentStartM < 18 * 60
  );
}

/** TZ-mismatch wall-clock artifacts (00:00–12:00, 20:00–12:00, etc.). */
export function looksLikeCorruptTzArtifact(start: string, end: string): boolean {
  if (start === '00:00' && end === '12:00') return true;
  const startM = parseHhmmToMinutes(start);
  const endM = parseHhmmToMinutes(end);
  // Evening-to-noon TZ mismatch (e.g. 20:00 start with 12:00 end on the day label).
  if (startM >= 18 * 60 && endM === 12 * 60) return true;
  return false;
}

function sessionBoundsInstantsAreInvalid(day: IScheduleDayConstraints): boolean {
  return new Date(day.endTime).getTime() < new Date(day.startTime).getTime();
}

/** Fix end-before-start when venue wall-clock clearly spans past midnight (e.g. 01:00–23:45). */
function repairOvernightSessionBounds(
  day: IScheduleDayConstraints,
  timeZone: string
): IScheduleDayConstraints {
  if (!sessionBoundsInstantsAreInvalid(day)) return day;

  const startHhmm = formatHhmmInTimeZone(day.startTime, timeZone);
  const endHhmm = formatHhmmInTimeZone(day.endTime, timeZone);
  if (looksLikeCorruptTzArtifact(startHhmm, endHhmm)) return day;

  const startM = parseHhmmToMinutes(startHhmm);
  const endM = parseHhmmToMinutes(endHhmm);
  if (endM <= startM && !looksLikeVenueSession(startHhmm, endHhmm)) return day;

  return {
    ...day,
    endTime: getTimeDateFromFormikInput(endHhmm, day.startTime, timeZone, {
      referenceStartIso: day.startTime,
    }),
  };
}

function repairAllOvernightSessionBounds(
  constraints: IScheduleConstraints,
  timeZone: string
): IScheduleConstraints {
  return {
    ...constraints,
    days: constraints.days.map((day) => repairOvernightSessionBounds(day, timeZone)),
  };
}

export function shouldRemapDayBoundsOnTimezoneSync(
  constraints: IScheduleConstraints,
  staleTimeZone: string,
  tournamentTimeZone: string
): boolean {
  if (!constraints.days.length || staleTimeZone === tournamentTimeZone) {
    return false;
  }

  return constraints.days.some((day) =>
    dayHasTimeEncodingMismatch(day, staleTimeZone, tournamentTimeZone)
  );
}

function dayHasTimeEncodingMismatch(
  day: IScheduleDayConstraints,
  staleTimeZone: string,
  tournamentTimeZone: string
): boolean {
  const inStale = {
    start: formatHhmmInTimeZone(day.startTime, staleTimeZone),
    end: formatHhmmInTimeZone(day.endTime, staleTimeZone),
  };
  const inTournament = {
    start: formatHhmmInTimeZone(day.startTime, tournamentTimeZone),
    end: formatHhmmInTimeZone(day.endTime, tournamentTimeZone),
  };

  if (inStale.start === inTournament.start || !looksLikeIntentionalDaySession(inStale.start, inStale.end)) {
    return false;
  }

  if (
    looksLikeCorruptTzArtifact(inTournament.start, inTournament.end) ||
    sessionBoundsInstantsAreInvalid(day)
  ) {
    return true;
  }

  return boundsAuthoredInStaleTimezone(inStale, inTournament);
}

function needsDaySessionBoundsRestore(
  day: IScheduleDayConstraints,
  timeZone: string,
  dayIndex: number,
  tournamentStartIso?: string
): boolean {
  const start = formatHhmmInTimeZone(day.startTime, timeZone);
  const end = formatHhmmInTimeZone(day.endTime, timeZone);
  if (looksLikeCorruptTzArtifact(start, end) || sessionBoundsInstantsAreInvalid(day)) {
    return true;
  }
  if (dayIndex === 0) {
    if (!looksLikeVenueSession(start, end)) return true;
    if (tournamentStartIso) {
      const { time: tournamentStartClock } = getVenueLocalParts(tournamentStartIso, timeZone);
      if (start !== tournamentStartClock) return true;
    }
  }
  return false;
}

function remapConstraintDaysSelectively(
  constraints: IScheduleConstraints,
  staleTimeZone: string,
  tournamentTimeZone: string
): { constraints: IScheduleConstraints; remappedDayKeys: Set<number> } {
  if (staleTimeZone === tournamentTimeZone) {
    return { constraints, remappedDayKeys: new Set() };
  }

  const remappedDayKeys = new Set<number>();
  const remap = (value: string) =>
    remapInstantAcrossTimeZone(value, staleTimeZone, tournamentTimeZone).toISOString();

  const days = constraints.days.map((day) => {
    if (!dayHasTimeEncodingMismatch(day, staleTimeZone, tournamentTimeZone)) {
      return day;
    }
    remappedDayKeys.add(day.dayKey);
    return {
      ...day,
      startTime: remap(day.startTime),
      endTime: remap(day.endTime),
    };
  });

  return {
    constraints: { ...constraints, days },
    remappedDayKeys,
  };
}

function restoreDaySessionBoundsFromTournament(
  constraints: IScheduleConstraints,
  tournamentStartIso: string,
  tournamentEndIso: string,
  timeZone: string,
  skipDayKeys: ReadonlySet<number> = new Set()
): IScheduleConstraints {
  const startParts = getVenueLocalParts(tournamentStartIso, timeZone);
  const endParts = getVenueLocalParts(tournamentEndIso, timeZone);

  return {
    ...constraints,
    days: constraints.days.map((day, index) => {
      if (skipDayKeys.has(day.dayKey)) return day;
      // Day 1+ custom sessions are preserved by selective remap / overnight repair only.
      if (index > 0) return day;
      if (!needsDaySessionBoundsRestore(day, timeZone, index, tournamentStartIso)) return day;

      const sessionStart = startParts.time;

      return {
        ...day,
        startTime:
          index === 0
            ? tournamentStartIso
            : getTimeDateFromFormikInput(sessionStart, day.startTime, timeZone),
        endTime: getTimeDateFromFormikInput(endParts.time, day.startTime, timeZone, {
          referenceStartIso: day.startTime,
        }),
      };
    }),
  };
}

export function reconcileScheduleTimezoneMetadata(
  constraints: IScheduleConstraints,
  tournamentTimeZone: string,
  tournamentDates?: { startDate?: string | null; endDate?: string | null }
): IScheduleConstraints {
  return {
    ...constraints,
    scheduleTimezone: tournamentTimeZone,
    ...(tournamentDates?.startDate ? { tournamentStartTime: tournamentDates.startDate } : {}),
    ...(tournamentDates?.endDate ? { tournamentEndTime: tournamentDates.endDate } : {}),
  };
}

export function reconcileScheduleTimezoneOnSync(
  constraints: IScheduleConstraints,
  staleTimeZone: string,
  tournamentTimeZone: string,
  tournamentDates?: { startDate?: string | null; endDate?: string | null }
): IScheduleConstraints {
  const repairedConstraints = repairAllOvernightSessionBounds(
    constraints,
    tournamentTimeZone
  );

  const { constraints: remappedConstraints, remappedDayKeys } =
    staleTimeZone !== tournamentTimeZone
      ? remapConstraintDaysSelectively(
          repairedConstraints,
          staleTimeZone,
          tournamentTimeZone
        )
      : { constraints: repairedConstraints, remappedDayKeys: new Set<number>() };

  const remappedAndRepaired = repairAllOvernightSessionBounds(
    remappedConstraints,
    tournamentTimeZone
  );

  let reconciled = reconcileScheduleTimezoneMetadata(
    remappedAndRepaired,
    tournamentTimeZone,
    tournamentDates
  );

  if (
    tournamentDates?.startDate &&
    tournamentDates?.endDate &&
    detectScheduleDayMismatch(
      tournamentDates.startDate,
      tournamentDates.endDate,
      reconciled,
      tournamentTimeZone
    )
  ) {
    reconciled = alignScheduleDaysToTournament(
      reconciled,
      tournamentDates.startDate,
      tournamentDates.endDate,
      tournamentTimeZone
    );
  }

  if (tournamentDates?.startDate && tournamentDates?.endDate) {
    reconciled = restoreDaySessionBoundsFromTournament(
      reconciled,
      tournamentDates.startDate,
      tournamentDates.endDate,
      tournamentTimeZone,
      remappedDayKeys
    );
  }

  return reconciled;
}

export function hasStoredTimezoneChanged(
  storedTimeZone: string | null | undefined,
  selectedTimeZone: string | null | undefined
): boolean {
  return Boolean(selectedTimeZone) && selectedTimeZone !== (storedTimeZone ?? '');
}

export function resolveLocationTimezoneMigrationPrevious(
  storedTimeZone: string | null | undefined,
  scheduleTimezone: string | null | undefined,
  countryFallbackTimeZone: string
): string {
  return storedTimeZone ?? scheduleTimezone ?? countryFallbackTimeZone;
}

export function needsLocationTimezoneMigration(
  storedTimeZone: string | null | undefined,
  selectedTimeZone: string,
  resolvedPreviousTimeZone: string
): boolean {
  return (
    hasStoredTimezoneChanged(storedTimeZone, selectedTimeZone) &&
    resolvedPreviousTimeZone !== selectedTimeZone
  );
}
